import config from "../../../config";
import { sendAuthenticatedAsyncRequest } from "../../../services/AsyncRequestService";

/**
 * Service for handling all API calls related to Unprocessed Fax Details
 */
class UnprocessedFaxDetailsService {
  /**
   * Fetch unprocessed fax details
   */
  async fetchUnprocessedFaxDetails(faxInfoId) {
    const uri = `/fax/unprocessed-fax-details?fax_info_id=${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax details: ${statusText}`));
        }
      );
    });
  }

  /**
   * Link unprocessed fax pages to switch
   */
  async linkUnprocessedFaxToSwitch(faxId, linkPages, isFaxCompletelyLinked = false) {
    const transformedLinkPages = linkPages.map((page) => ({
      selectedGlobalSwitchId: page.identifier,
      isUpsideDown: page.isUpsideDown || false,
      pageIndex: page.referenceField ? parseInt(page.referenceField, 10) : null,
      overrideAssociatedSwitch: page.overrideAssociatedSwitch || false,
      additionalPages: page.additionalField || null,
    }));

    const uri = `/fax/link-fax-page`;
    const body = {
      faxInfoId: faxId,
      noOfLinkPages: transformedLinkPages.length,
      linkPages: transformedLinkPages,
      isFaxCompletelyLinked: isFaxCompletelyLinked,
    };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        body,
        () => {
          resolve();
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to link fax: ${message}`));
        }
      );
    });
  }

  /**
   * Discard fax
   */
  async discardFax(faxInfoId) {
    const uri = `/fax/discard-fax?fax_info_id=${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to discard fax: ${statusText}`));
        }
      );
    });
  }

  /**
   * Set priority of unprocessed fax
   */
  async setPriorityOfUnprocessedFax(faxId, priority) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/priority`;
    const body = { priority: priority || "" };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        body,
        () => {
          resolve({ success: true });
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to update priority: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch note for unlinked fax
   */
  async fetchNoteForUnlinkedFax(faxId) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/get-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> null instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve(null);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch note: ${statusText}`));
        }
      );
    });
  }

  /**
   * Save note
   */
  async saveNote(faxId, noteObject) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/fax-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        noteObject,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to save note: ${message}`));
        }
      );
    });
  }

  /**
   * Update note
   */
  async updateNote(faxId, noteObject) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/fax-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        noteObject,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to update note: ${message}`));
        }
      );
    });
  }

  /**
   * Delete note
   */
  async deleteNote(noteId) {
    const uri = `/fax/unprocessed-fax-details/${noteId}/delete-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to delete note: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch all available tags
   */
  async fetchAllTags() {
    const uri = `/api/fax-tags`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          const data = response.data || [];
          resolve(data.map((tag) => tag.faxTagId));
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch tags: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch fax program codes (for Unknown fax type tags)
   */
  async fetchFaxProgramTags() {
    const uri = `/fax-program/codes`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          const data = response.data;
          resolve(Array.isArray(data) ? data : []);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax program codes: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch tags for a specific fax
   */
  async fetchFaxTags(faxInfoId) {
    const uri = `/api/faxes/${faxInfoId}/tags`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> [] instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve([]);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax tags: ${statusText}`));
        }
      );
    });
  }

  /**
   * Save tags for a fax
   */
  async saveTags(faxInfoId, tags) {
    const uri = `/api/faxes/${faxInfoId}/tags`;
    const body = { tags };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        body,
        () => {
          resolve({ success: true });
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to save tags: ${message}`));
        }
      );
    });
  }

  /**
   * Fetch existing linked NPIs/pages for a fax (for NON_SWITCH)
   */
  async fetchLinkedNpisForFax(faxInfoId) {
    const uri = `/fax-archive/fax-info/${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> [] instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve([]);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch linked NPIs: ${statusText}`));
        }
      );
    });
  }

  /**
   * Complete linking page for NON_SWITCH fax
   */
  async completePageLinking(faxInfoId) {
    const uri = `/fax/complete-linkage/${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        null,
        () => {
          resolve();
        },
        (error) => {
          let errorMessage = "Failed to complete linking";
          if (error?.data?.message) {
            errorMessage = error.data.message;
          } else if (error?.statusText) {
            errorMessage = `Failed to complete linking: ${error.statusText}`;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          reject(new Error(errorMessage));
        }
      );
    });
  }
  

  /**
   * Link NPI to fax (NON_SWITCH workflow)
   */
  async linkAndArchiveFax(faxInfoId, linkPages, isFaxCompletelyLinked = false) {
    // Prepare the request body with relevant data for linking NPI to fax
    const requestBody = {
      faxInfoId: faxInfoId,
      isFaxCompletelyLinked: isFaxCompletelyLinked,
      linkPages: linkPages.map((page) => ({
        prescriberNpi: page.identifier, // NPI identifier
        faxReferenceId: page.referenceField, // Fax Reference ID
        linkPages: page.additionalField, // Link Pages (comma-separated or array)
        isUpsideDown: page.isUpsideDown || false,
      })),
    };

    const uri = `/fax-archive`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        requestBody,
        (response) => {
          // API previously returned empty body; keep behavior but allow any data
          resolve(response?.data || { success: true });
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to link NPI to fax: ${message}`));
        }
      );
    });
  }
}

export default new UnprocessedFaxDetailsService();

